import { useOrganization, useUser } from "@clerk/expo";
import {
  Archive,
  Calendar,
  Copy,
  Edit2,
  FolderInput,
  UserPlus,
} from "lucide-react-native";
import { FC, useMemo, useState } from "react";

import { MoveToModal } from "@/components/folders";
import { ShareModal } from "@/components/studies";
import {
  ActionMenu,
  Badge,
  BadgeText,
  Box,
  Card,
  DeleteDialog,
  HStack,
  Image,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import {
  useDatetimeUtils,
  useLocalization,
  useOrganizationMembers,
} from "@/hooks";
import API from "@/hooks/api";
import { Panel as PanelType } from "@/types/resources";
import DemographicSettings from "./DemographicSettings";
import PanelEditor from "./PanelEditor";

interface PanelProps {
  onDeleted?: () => void;
  onDuplicated?: (panel: PanelType) => void;
  onMoved?: () => void;
  onSaved?: (panel: PanelType) => void;
  panel: PanelType;
}

const Panel: FC<PanelProps> = ({
  onDeleted,
  onDuplicated,
  onMoved,
  onSaved,
  panel,
}) => {
  const { user } = useUser();
  const { organization } = useOrganization();
  const { formatMemberName, users: membersMap } = useOrganizationMembers();
  const isAdmin = user?.publicMetadata?.admin === true;
  const author = panel.created_by_id
    ? membersMap[panel.created_by_id]
    : undefined;
  const { formatDate } = useDatetimeUtils();
  const { i18n } = useLocalization();
  const isArchived = !!panel.archived_at;

  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [editingPanel, setEditingPanel] = useState<PanelType | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const {
    archivePanel,
    error: archiveError,
    loading: archiving,
  } = API.Panels.useArchivePanel();

  const { updatePanel } = API.Panels.useUpdatePanel();

  const menuActions = useMemo(() => {
    if (isArchived) return null;

    const actions = [
      ...(isAdmin && panel.can_edit
        ? [
            {
              color: "typography" as const,
              icon: Edit2,
              label: i18n.t("misc.edit"),
              onPress: () => {
                setIsDuplicating(false);
                setEditingPanel(panel);
              },
            },
          ]
        : []),
      ...(isAdmin
        ? [
            {
              color: "typography" as const,
              icon: Copy,
              label: i18n.t("misc.duplicate"),
              onPress: () => {
                setIsDuplicating(true);
                setEditingPanel({
                  ...panel,
                  id: "",
                  is_default: false,
                  name: i18n.t("studies.copyOf", { name: panel.name }),
                });
              },
            },
          ]
        : []),
      ...(panel.can_edit && organization
        ? [
            {
              color: "typography" as const,
              divider: true as const,
              icon: UserPlus,
              label: i18n.t("sharing.share"),
              onPress: () => setShowShareModal(true),
            },
          ]
        : []),
      ...(panel.can_edit
        ? [
            {
              color: "typography" as const,
              divider: !organization ? (true as const) : undefined,
              icon: FolderInput,
              label: i18n.t("misc.move"),
              onPress: () => setShowMoveModal(true),
            },
          ]
        : []),
      ...(isAdmin && panel.can_edit
        ? [
            {
              color: "error" as const,
              divider: true as const,
              icon: Archive,
              label: i18n.t("misc.archive"),
              onPress: () => setShowArchiveDialog(true),
            },
          ]
        : []),
    ];
    return actions.length > 0 ? actions : null;
  }, [i18n, isAdmin, isArchived, organization, panel]);

  const handleArchiveConfirm = async () => {
    const success = await archivePanel(panel.id);
    if (success) {
      setShowArchiveDialog(false);
      onDeleted?.();
    }
  };

  const handlePanelSaved = (savedPanel: PanelType) => {
    const wasDuplicating = isDuplicating;
    setEditingPanel(null);
    setIsDuplicating(false);
    if (wasDuplicating) {
      onDuplicated?.(savedPanel);
    } else {
      onSaved?.(savedPanel);
    }
  };

  return (
    <>
      <Box className="flex-1">
        <Card className="h-full border border-outline-200 rounded-2xl p-4">
          <VStack className="gap-6">
            <HStack className="items-start justify-between">
              <VStack className="flex-1 gap-0.5">
                <HStack className="items-center gap-1.5">
                  <Text className="text-base font-semibold text-typography-900">
                    {panel.name}
                  </Text>
                  {panel.is_default && (
                    <Badge
                      action="warning"
                      className="px-2 py-0.5 rounded-full"
                      variant="solid"
                    >
                      <BadgeText className="normal-case text-2xs font-medium">
                        {i18n.t("panels.default").toLowerCase()}
                      </BadgeText>
                    </Badge>
                  )}
                </HStack>
                <HStack className="items-center gap-3 my-2">
                  {author && (
                    <HStack className="items-center gap-1.5">
                      <Image
                        alt={formatMemberName(author, true)}
                        className="w-4 h-4 rounded-full"
                        source={{ uri: author.imageUrl }}
                      />
                      <Text className="text-xs font-medium text-typography-500">
                        {formatMemberName(author, true)}
                      </Text>
                    </HStack>
                  )}
                  <HStack className="items-center gap-1">
                    <Calendar
                      color="rgb(var(--color-typography-500))"
                      size={14}
                      strokeWidth={2}
                    />
                    <Text className="text-xs text-typography-500">
                      {formatDate(panel.created_at)}
                    </Text>
                  </HStack>
                </HStack>
              </VStack>

              {menuActions && <ActionMenu actions={menuActions} />}
            </HStack>

            {!!panel.description &&
              (() => {
                const maxChars = 350;
                const isTruncatable = panel.description.length > maxChars;
                const displayText =
                  !descriptionExpanded && isTruncatable
                    ? `${panel.description.slice(0, maxChars).trimEnd()}…`
                    : panel.description;
                return (
                  <VStack className="gap-2">
                    <Text className="text-sm font-semibold text-typography-900">
                      {i18n.t("panels.description")}
                    </Text>
                    <Text className="text-sm text-typography-500">
                      {displayText}
                    </Text>
                    {isTruncatable && (
                      <Pressable
                        onPress={() => setDescriptionExpanded((v) => !v)}
                      >
                        <Text className="text-xs font-semibold text-typography-400">
                          {descriptionExpanded
                            ? i18n.t("misc.seeLess")
                            : i18n.t("misc.seeMore")}
                        </Text>
                      </Pressable>
                    )}
                  </VStack>
                );
              })()}

            <VStack className="gap-2">
              <Text className="text-sm font-semibold text-typography-900">
                {i18n.t("panels.demographics")}
              </Text>
              <DemographicSettings demographics={panel.demographics} />
            </VStack>
          </VStack>
        </Card>
      </Box>

      <DeleteDialog
        body={i18n.t("panels.confirmArchiveMessage")}
        deleteButtonText={i18n.t("misc.archive")}
        error={archiveError}
        isOpen={showArchiveDialog}
        loading={archiving}
        loadingText={i18n.t("misc.archiving")}
        onCancel={() => setShowArchiveDialog(false)}
        onDelete={handleArchiveConfirm}
        title={i18n.t("panels.confirmArchive")}
      />

      <PanelEditor
        onClose={() => setEditingPanel(null)}
        onSave={handlePanelSaved}
        panel={editingPanel}
      />

      {showMoveModal && (
        <MoveToModal
          currentFolderId={panel.folder_id}
          isOpen={true}
          itemName={panel.name}
          onClose={() => setShowMoveModal(false)}
          onMove={async (targetFolderId) => {
            const result = await updatePanel(panel.id, {
              demographics: panel.demographics,
              description: null,
              folder_id: targetFolderId,
              is_default: panel.is_default,
              name: panel.name,
            });
            if (result) onMoved?.();
            return !!result;
          }}
          type="panels"
        />
      )}

      {showShareModal && (
        <ShareModal
          isOpen
          onClose={() => setShowShareModal(false)}
          ownerId={panel.created_by_id}
          resourceId={panel.id}
          resourceName={panel.name}
          resourceType="panel"
        />
      )}
    </>
  );
};

export default Panel;
